import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../ui/checkbox';
import { STATUS_OPTIONS, normalizeStatus } from '../../lib/statusCatalog';

// v5.2.0 catalog (10 canonical statuses) — see /lib/statusCatalog.js
const statusOptions = STATUS_OPTIONS;

export const UnitModal = ({ 
  open, 
  onOpenChange, 
  unit, 
  equipment, 
  locations, 
  categories = [],
  api, 
  onSuccess 
}) => {
  const [unitForm, setUnitForm] = useState({
    serial_number: '',
    location_id: '',
    status: 'disponible',
    notes: '',
    receptor_number: '',
    fault_reason: '',
    no_serial: false
  });
  const [saving, setSaving] = useState(false);
  const [showFaultReason, setShowFaultReason] = useState(false);
  const [previousStatus, setPreviousStatus] = useState('');

  useEffect(() => {
    if (unit) {
      const isNA = unit.serial_number === 'NA' || unit.serial_number === 'N/A';
      setUnitForm({
        serial_number: unit.serial_number || '',
        location_id: unit.location_id || '',
        status: unit.status || 'disponible',
        notes: unit.notes || '',
        receptor_number: unit.receptor_number || '',
        fault_reason: unit.fault_reason || '',
        no_serial: isNA
      });
      setPreviousStatus(unit.status || 'disponible');
      setShowFaultReason(unit.status === 'falla');
    } else {
      setUnitForm({
        serial_number: '',
        location_id: '',
        status: 'disponible',
        notes: '',
        receptor_number: '',
        fault_reason: '',
        no_serial: false
      });
      setPreviousStatus('');
      setShowFaultReason(false);
    }
  }, [unit, open]);

  const handleStatusChange = (newStatus) => {
    setUnitForm(prev => ({ ...prev, status: newStatus }));
    if (newStatus === 'falla') {
      setShowFaultReason(true);
    } else {
      setShowFaultReason(false);
    }
  };

  const handleSaveUnit = async () => {
    if (!unitForm.no_serial && !unitForm.serial_number) {
      toast.error('Ingresa un numero de serie o marca "Sin número de serie"');
      return;
    }
    if (!unitForm.location_id) {
      toast.error('Selecciona una ubicación');
      return;
    }

    if (unitForm.status === 'falla' && (!unitForm.fault_reason || unitForm.fault_reason.trim().length < 5)) {
      toast.error('Debes agregar una justificacion de la falla (minimo 5 caracteres)');
      return;
    }

    setSaving(true);
    try {
      const apiInstance = api();
      const payload = { ...unitForm };
      delete payload.no_serial;
      
      // If no_serial is checked, set serial to NA
      if (unitForm.no_serial) {
        payload.serial_number = 'NA';
      }
      
      // Flag if changing from falla to disponible (to auto-resolve maintenance)
      if (previousStatus === 'falla' && unitForm.status === 'disponible') {
        payload.resolve_maintenance = true;
      }
      
      // Flag if changing to falla (to auto-create maintenance)
      if (unitForm.status === 'falla' && previousStatus !== 'falla') {
        payload.auto_create_maintenance = true;
      }

      if (unit) {
        await apiInstance.put(`/equipment-units/${unit.id}`, payload);
        toast.success('Unidad actualizada');
      } else {
        await apiInstance.post(`/equipment/${equipment.id}/units`, {
          equipment_id: equipment.id,
          ...payload
        });
        toast.success('Unidad creada');
      }
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    setUnitForm({
      serial_number: '',
      location_id: '',
      status: 'disponible',
      notes: '',
      receptor_number: '',
      fault_reason: '',
      no_serial: false
    });
    setShowFaultReason(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{unit ? 'Editar Unidad' : 'Nueva Unidad'}</DialogTitle>
          <DialogDescription>
            {equipment?.name} - Cada unidad es un equipo individual con su numero de serie
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Numero de Serie {!unitForm.no_serial && '*'}</Label>
            <Input 
              value={unitForm.no_serial ? 'NA' : unitForm.serial_number} 
              onChange={(e) => setUnitForm({ ...unitForm, serial_number: e.target.value })} 
              placeholder="Ej: SN-2024-001"
              className="font-mono"
              disabled={unitForm.no_serial}
            />
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <Checkbox
                checked={unitForm.no_serial}
                onCheckedChange={(checked) => {
                  setUnitForm({ 
                    ...unitForm, 
                    no_serial: checked,
                    serial_number: checked ? 'NA' : (unitForm.serial_number === 'NA' ? '' : unitForm.serial_number)
                  });
                }}
                data-testid="no-serial-checkbox"
              />
              <span className="text-sm text-muted-foreground">Sin número de serie</span>
            </label>
            {(unitForm.serial_number === 'NA' || unitForm.serial_number === 'N/A' || unitForm.no_serial) && (
              <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-md" data-testid="no-serial-warning">
                <Info className="h-4 w-4 text-amber-500 shrink-0" />
                <p className="text-xs text-amber-400">
                  Esta unidad no tiene número de serie. Se recomienda asignar uno cuando esté disponible.
                </p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Ubicacion *</Label>
            <Select value={unitForm.location_id} onValueChange={(v) => setUnitForm({ ...unitForm, location_id: v })}>
              <SelectTrigger><SelectValue placeholder="Seleccionar ubicacion" /></SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Estado</Label>
            <Select value={normalizeStatus(unitForm.status) || unitForm.status} onValueChange={handleStatusChange}>
              <SelectTrigger data-testid="unit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fault reason - shown when status is falla */}
          {showFaultReason && (
            <div className="space-y-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="fault-reason-section">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="h-4 w-4" />
                <Label className="text-red-400 font-medium">Justificacion de la falla *</Label>
              </div>
              <Textarea 
                value={unitForm.fault_reason} 
                onChange={(e) => setUnitForm({ ...unitForm, fault_reason: e.target.value })} 
                placeholder="Describe que paso con el equipo (minimo 5 caracteres)..."
                rows={3}
                className="border-red-500/30"
                data-testid="fault-reason-input"
              />
              <p className="text-xs text-muted-foreground">
                Se creara automaticamente un registro de mantenimiento correctivo
              </p>
            </div>
          )}

          {/* Info when resolving falla */}
          {previousStatus === 'falla' && unitForm.status === 'disponible' && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-testid="resolve-maintenance-info">
              <p className="text-sm text-emerald-400 font-medium">Se resolvera el mantenimiento pendiente</p>
              <p className="text-xs text-muted-foreground mt-1">
                El registro de mantenimiento asociado se marcara como completado automaticamente
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>No. de {categories?.find(c => c.id === equipment?.category_id)?.name || equipment?.name || 'Unidad'}</Label>
            <Input 
              value={unitForm.receptor_number} 
              onChange={(e) => setUnitForm({ ...unitForm, receptor_number: e.target.value })} 
              placeholder={`Ej: ${categories?.find(c => c.id === equipment?.category_id)?.name || equipment?.name || 'Unidad'} #1`}
              data-testid="unit-receptor-number"
            />
          </div>

          <div className="space-y-2">
            <Label>Notas de la Unidad</Label>
            <Textarea 
              value={unitForm.notes} 
              onChange={(e) => setUnitForm({ ...unitForm, notes: e.target.value })} 
              placeholder="Notas especificas de esta unidad"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSaveUnit} disabled={saving} className="btn-primary">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {unit ? 'Guardar' : 'Crear Unidad'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnitModal;
